import React, { useEffect, useState } from 'react';
import Card from '../components/Card';
import Button from '../components/Button';
import { inputStyle } from '../components/FormField';
import * as forecastApi from '../services/forecastingApi';
import { formatDate } from '../utils/dateFormat';
import DateInput from '../components/DateInput';

function fmtDate(d) {
  return formatDate(d);
}

const BORDER = '1px solid var(--color-border)';
const thLined = { padding: '8px 4px', fontSize: 'var(--font-size-xs)', fontWeight: 700, color: '#1e293b', borderRight: BORDER };
const tdLined = { padding: '8px 4px', fontSize: 'var(--font-size-sm)', borderRight: BORDER };

/** One "15 Day" / "30 Day" reorder forecast card — its own heading already
 * summarizes the whole prediction size-wise, one size per line plus a
 * TOTAL line, and clicking it expands the full table below, with a find
 * box above Customer, Design, Size, and Ref. Party so any of the four can
 * be searched directly instead of scanning a long list by eye. */
function ReorderForecastCard({ title, windowDays }) {
  const [expanded, setExpanded] = useState(false);
  const [card, setCard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [customerSearch, setCustomerSearch] = useState('');
  const [designSearch, setDesignSearch] = useState('');
  const [sizeSearch, setSizeSearch] = useState('');
  const [refPartySearch, setRefPartySearch] = useState('');
  const [sort, setSort] = useState({ key: null, direction: 'asc' });

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    forecastApi.getReorderCard(windowDays)
      .then((data) => { if (!cancelled) setCard(data); })
      .catch((err) => { if (!cancelled) setError(err.message || 'Could not load.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [windowDays]);

  // The size-wise heading stays based on the FULL prediction set regardless
  // of the searches below — only the detail table rows are narrowed, so
  // "find a customer" never changes what the card's own headline total
  // means. One size per line, plus a TOTAL line summing every size.
  const headingTotal = card ? card.sizeHeadingTotals.reduce((sum, s) => sum + s.qty, 0) : 0;

  const customerQuery = customerSearch.trim().toLowerCase();
  const designQuery = designSearch.trim().toLowerCase();
  const sizeQuery = sizeSearch.trim().toLowerCase();
  const refPartyQuery = refPartySearch.trim().toLowerCase();
  const filteredRows = React.useMemo(() => (
    card
      ? card.rows.filter((r) => {
          const matchesCustomer = !customerQuery || (r.customerName || '').toLowerCase().includes(customerQuery);
          const matchesDesign = !designQuery || (r.designName || '').toLowerCase().includes(designQuery);
          const matchesSize = !sizeQuery || (r.sizeName || '').toLowerCase().includes(sizeQuery);
          const matchesRefParty = !refPartyQuery || (r.refPartyName || '').toLowerCase().includes(refPartyQuery);
          return matchesCustomer && matchesDesign && matchesSize && matchesRefParty;
        })
      : []
  ), [card, customerQuery, designQuery, sizeQuery, refPartyQuery]);

  // The "Approx Qty" grand total reflects whatever is currently visible —
  // the full list normally, or just the matching rows while a search is
  // active, so the footer always adds up to exactly what's shown above it.
  const filteredQtyTotal = filteredRows.reduce((sum, r) => sum + r.predictedQty, 0);

  const handleSort = (key) => {
    setSort((prev) => (prev.key === key
      ? { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
      : { key, direction: 'asc' }));
  };

  // Sorting happens after filtering — the grand total above already summed
  // the unsorted filtered set, so changing sort order never changes what
  // that total means, only the order rows appear in.
  const sortedRows = React.useMemo(() => {
    if (!sort.key) return filteredRows;
    const field = sort.key === 'qty' ? 'predictedQty' : 'predictedDate';
    const copy = [...filteredRows];
    copy.sort((a, b) => {
      const av = sort.key === 'qty' ? a[field] : new Date(a[field]).getTime();
      const bv = sort.key === 'qty' ? b[field] : new Date(b[field]).getTime();
      return sort.direction === 'asc' ? av - bv : bv - av;
    });
    return copy;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredRows, sort]);

  const findBoxStyle = {
    width: '100%',
    marginTop: 4,
    padding: '4px 6px',
    fontSize: 'var(--font-size-xs)',
    fontWeight: 400,
    border: '1px solid var(--color-border)',
    borderRadius: 4,
    boxSizing: 'border-box',
  };

  return (
    <div
      style={{
        background: 'var(--color-surface)',
        border: expanded ? '1px solid var(--color-accent)' : '1px solid var(--color-border)',
        borderRadius: 'var(--radius-md)',
        padding: 'var(--space-4)',
        marginBottom: 'var(--space-4)',
      }}
    >
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        style={{
          background: 'none', border: 'none', padding: 0, cursor: 'pointer', textAlign: 'left', width: '100%', font: 'inherit',
        }}
      >
        <div style={{ fontSize: 'var(--font-size-sm)', fontWeight: 600, color: 'var(--color-text-muted)', marginBottom: 4 }}>
          {title}{expanded ? ' \u25B2' : ' \u25BC'}
        </div>
        {loading ? (
          <p style={{ color: 'var(--color-text-secondary)', margin: 0 }}>Loading…</p>
        ) : error ? (
          <p style={{ color: 'var(--color-danger)', margin: 0 }}>{error}</p>
        ) : card && card.sizeHeadingTotals.length > 0 ? (
          <div style={{ fontSize: 'var(--font-size-lg)', fontWeight: 700, color: 'var(--color-accent)', lineHeight: 1.6 }}>
            {card.sizeHeadingTotals.map((s) => (
              <div key={s.sizeName}>{s.sizeName} = {s.qty.toLocaleString('en-IN')} Box</div>
            ))}
            <div style={{ borderTop: '1px solid var(--color-border)', marginTop: 4, paddingTop: 4, color: 'var(--color-text-primary)' }}>
              TOTAL = {headingTotal.toLocaleString('en-IN')} Box
            </div>
          </div>
        ) : (
          <div style={{ fontSize: 'var(--font-size-lg)', fontWeight: 700, color: 'var(--color-accent)' }}>
            No predicted reorders in this window
          </div>
        )}
      </button>

      {expanded && card && (
        <div className="table-scroll" style={{ marginTop: 'var(--space-4)' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', border: BORDER }}>
            <thead>
              <tr style={{ textAlign: 'left', borderBottom: BORDER, background: '#e2e8f0' }}>
                <th style={thLined}>
                  Customer
                  <input
                    type="text"
                    placeholder="Find customer…"
                    value={customerSearch}
                    onChange={(e) => setCustomerSearch(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                    style={findBoxStyle}
                  />
                </th>
                <th style={thLined}>
                  Design
                  <input
                    type="text"
                    placeholder="Find design…"
                    value={designSearch}
                    onChange={(e) => setDesignSearch(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                    style={findBoxStyle}
                  />
                </th>
                <th style={thLined}>
                  Size
                  <input
                    type="text"
                    placeholder="Find size…"
                    value={sizeSearch}
                    onChange={(e) => setSizeSearch(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                    style={findBoxStyle}
                  />
                </th>
                <th
                  role="button"
                  tabIndex={0}
                  onClick={() => handleSort('qty')}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleSort('qty'); }}
                  style={{ ...thLined, cursor: 'pointer', userSelect: 'none', color: sort.key === 'qty' ? 'var(--color-accent)' : thLined.color }}
                >
                  Approx Qty{sort.key === 'qty' ? (sort.direction === 'asc' ? ' \u2191' : ' \u2193') : ''}
                </th>
                <th
                  role="button"
                  tabIndex={0}
                  onClick={() => handleSort('date')}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleSort('date'); }}
                  style={{ ...thLined, cursor: 'pointer', userSelect: 'none', color: sort.key === 'date' ? 'var(--color-accent)' : thLined.color }}
                >
                  Approx Date{sort.key === 'date' ? (sort.direction === 'asc' ? ' \u2191' : ' \u2193') : ''}
                </th>
                <th style={{ ...thLined, borderRight: 'none' }}>
                  Ref. Party
                  <input
                    type="text"
                    placeholder="Find ref. party…"
                    value={refPartySearch}
                    onChange={(e) => setRefPartySearch(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                    style={findBoxStyle}
                  />
                </th>
              </tr>
            </thead>
            <tbody>
              {sortedRows.map((r) => (
                <tr key={`${r.customerId}-${r.designId}-${r.sizeId}`} style={{ borderBottom: BORDER }}>
                  <td style={tdLined}>{r.customerName}</td>
                  <td style={tdLined}>{r.designName}</td>
                  <td style={tdLined}>{r.sizeName}</td>
                  <td className="data-figure" style={{ ...tdLined, fontWeight: 600 }}>{r.predictedQty.toLocaleString('en-IN')}</td>
                  <td className="data-figure" style={tdLined}>
                    {fmtDate(r.predictedDate)}
                    {r.daysUntil < 0 && (
                      <span style={{ color: 'var(--color-danger)', fontWeight: 600 }}> (overdue {Math.abs(r.daysUntil)}d)</span>
                    )}
                  </td>
                  <td style={{ ...tdLined, borderRight: 'none' }}>{r.refPartyName || '—'}</td>
                </tr>
              ))}
              {filteredRows.length === 0 && (
                <tr>
                  <td colSpan={6} style={{ padding: 'var(--space-4)', color: 'var(--color-text-muted)' }}>
                    {card.rows.length === 0
                      ? 'No reorders predicted in this window.'
                      : 'No rows match this search.'}
                  </td>
                </tr>
              )}
              {filteredRows.length > 0 && (
                <tr style={{ background: '#e2e8f0', borderTop: '2px solid var(--color-border)' }}>
                  <td colSpan={3} style={{ padding: '8px 4px', fontSize: 'var(--font-size-sm)', fontWeight: 700, color: '#1e293b', textAlign: 'right', borderRight: BORDER }}>
                    Grand Total
                  </td>
                  <td className="data-figure" style={{ padding: '8px 4px', fontSize: 'var(--font-size-sm)', fontWeight: 700, color: '#1e293b', borderRight: BORDER }}>
                    {filteredQtyTotal.toLocaleString('en-IN')}
                  </td>
                  <td colSpan={2} style={{ padding: '8px 4px' }} />
                </tr>
              )}
            </tbody>
          </table>
          <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', marginTop: 8 }}>
            Based on each customer&apos;s average reorder cycle over the last 2 years of dispatch
            history — dates are approximate. A combination with only one dispatch ever has no
            cycle to predict from and is left out.
          </p>
        </div>
      )}
    </div>
  );
}

export default function ForecastingPage() {
  const [horizon, setHorizon] = useState('short');
  const [periodFrom, setPeriodFrom] = useState('');
  const [periodTo, setPeriodTo] = useState('');
  const [sizeId, setSizeId] = useState('');
  const [sizes, setSizes] = useState([]);
  const [forecasts, setForecasts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);

  const load = () =>
    forecastApi.listForecasts({
      horizon,
      periodFrom: periodFrom || undefined,
      periodTo: periodTo || undefined,
      sizeId: sizeId || undefined,
    }).then(setForecasts);

  useEffect(() => {
    forecastApi.listSizes().then(setSizes).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    load()
      .catch((err) => setError(err.message || 'Could not load forecasts.'))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [horizon, periodFrom, periodTo, sizeId]);

  const handleRefresh = async () => {
    setRefreshing(true);
    setError(null);
    try {
      await forecastApi.refreshForecasts();
      await load();
    } catch (err) {
      setError(err.message || 'Refresh failed.');
    } finally {
      setRefreshing(false);
    }
  };

  // group by design for a cleaner display
  const byDesign = forecasts.reduce((acc, f) => {
    (acc[f.design_name] = acc[f.design_name] || []).push(f);
    return acc;
  }, {});

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <h1>Forecasting</h1>
          <p style={{ color: 'var(--color-text-secondary)' }}>
            Short-term (weekly) and long-term (monthly) demand projections with confidence
            intervals.
          </p>
        </div>
        <Button variant="secondary" onClick={handleRefresh} disabled={refreshing}>
          {refreshing ? 'Forecasting…' : 'Run forecasting'}
        </Button>
      </div>

      {error && <p style={{ color: 'var(--color-danger)' }}>{error}</p>}

      <ReorderForecastCard title="15 Day Reorder Forecast" windowDays={15} />
      <ReorderForecastCard title="30 Day Reorder Forecast" windowDays={30} />

      <Card
        title="Demand Forecast"
        actions={
          <select value={horizon} onChange={(e) => setHorizon(e.target.value)} style={{ ...inputStyle, width: 'auto' }}>
            <option value="short">Short-term (weekly)</option>
            <option value="long">Long-term (monthly)</option>
          </select>
        }
      >
        <div className="kpi-row" style={{ display: 'flex', gap: 'var(--space-3)', alignItems: 'flex-end', marginBottom: 'var(--space-4)' }}>
          <label style={{ display: 'flex', flexDirection: 'column', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)', gap: 4 }}>
            From
            <DateInput value={periodFrom} onChange={(e) => setPeriodFrom(e.target.value)} style={inputStyle} />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)', gap: 4 }}>
            To
            <DateInput value={periodTo} onChange={(e) => setPeriodTo(e.target.value)} style={inputStyle} />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)', gap: 4 }}>
            Size
            <select value={sizeId} onChange={(e) => setSizeId(e.target.value)} style={inputStyle}>
              <option value="">All sizes</option>
              {sizes.map((s) => (
                <option key={s.size_id} value={s.size_id}>{s.name}</option>
              ))}
            </select>
          </label>
          {(periodFrom || periodTo || sizeId) && (
            <Button variant="text" onClick={() => { setPeriodFrom(''); setPeriodTo(''); setSizeId(''); }}>
              Clear filters
            </Button>
          )}
        </div>

        {loading ? (
          <p style={{ color: 'var(--color-text-secondary)' }}>Loading…</p>
        ) : (
          Object.entries(byDesign).map(([designName, rows]) => (
            <div key={designName} style={{ marginBottom: 'var(--space-4)' }}>
              <h3 style={{ fontSize: 'var(--font-size-sm)', marginBottom: 8 }}>
                {designName}
                {rows[0].size_name && (
                  <span style={{ fontWeight: 400, color: 'var(--color-text-muted)', fontSize: 'var(--font-size-xs)' }}> · {rows[0].size_name}</span>
                )}
              </h3>
              <div className="table-scroll">
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ textAlign: 'left', borderBottom: '1px solid var(--color-border)' }}>
                    {['Period', 'Forecast', 'Range', 'Confidence'].map((h) => (
                      <th key={h} style={{ padding: '6px 4px', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.forecast_id} style={{ borderBottom: '1px solid var(--color-border)' }}>
                      <td className="data-figure" style={{ padding: '6px 4px', fontSize: 'var(--font-size-sm)' }}>{fmtDate(r.period_start)} – {fmtDate(r.period_end)}</td>
                      <td className="data-figure" style={{ padding: '6px 4px', fontSize: 'var(--font-size-sm)', fontWeight: 600 }}>{r.forecast_qty}</td>
                      <td className="data-figure" style={{ padding: '6px 4px', fontSize: 'var(--font-size-sm)', color: 'var(--color-text-muted)' }}>
                        {r.forecast_qty_low} – {r.forecast_qty_high}
                      </td>
                      <td className="data-figure" style={{ padding: '6px 4px', fontSize: 'var(--font-size-sm)' }}>{Math.round(r.confidence * 100)}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            </div>
          ))
        )}
        {!loading && Object.keys(byDesign).length === 0 && (
          <p style={{ color: 'var(--color-text-muted)' }}>
            No forecasts match these filters — import order history and run forecasting (needs
            at least 4 weeks of order data per design).
          </p>
        )}
      </Card>
    </div>
  );
}
